import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useSearchParams } from 'react-router-dom';
import { Flex, Spinner, useToast, Box, useDisclosure } from '@chakra-ui/react';
import moment from 'moment';
import { PageBox } from '../../../components/pageBox';
import { api } from '../../../services/api';
import { useDossie } from '../../../hooks/useDossie';
import {
  DossieHeader,
  DossieFilters,
  DossieDashboard,
  EnviarClienteModal,
  LinksLogsModal,
} from './components';
import {
  IDossieData,
  IDossieCompanyInfo,
  IDossieFilters,
  IFaturamentoItem,
  IImpostosTotals,
  IFolhaPorMesData,
} from './utils/dossieTypes';
import { exportDossieToPDF } from './utils/dossieExport';

export function DossieEmpresaPage() {
  const { externalId } = useParams<{ externalId: string }>();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const isFetching = useRef(false);
  const { getFaturamentoList, getImpostosTotais, getFolhaPorMes, getTaxParams } = useDossie();

  // Company info from navigation state or will be fetched
  const [companyInfo, setCompanyInfo] = useState<IDossieCompanyInfo | null>(
    location.state?.company
      ? {
          external_id: location.state.company.external_id,
          razao_social: location.state.company.razao_social || location.state.company.nome,
          nome: location.state.company.nome,
          cnpj: location.state.company.cnpj,
          regime_tributario_atualizado: location.state.company.regime_tributario_atualizado,
        }
      : null,
  );

  // Loading states
  const [isLoadingCompany, setIsLoadingCompany] = useState(!companyInfo);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  // Modal de enviar ao cliente
  const {
    isOpen: isEnviarModalOpen,
    onOpen: onOpenEnviarModal,
    onClose: onCloseEnviarModal,
  } = useDisclosure();

  // Modal de logs de links
  const {
    isOpen: isLogsModalOpen,
    onOpen: onOpenLogsModal,
    onClose: onCloseLogsModal,
  } = useDisclosure();

  // Data state
  const [dossieData, setDossieData] = useState<IDossieData>({
    faturamento: [],
    impostos: null,
    folha: null,
  });

  // Date filters - default to previous month, but recover from URL if available
  const getDefaultFilters = (): IDossieFilters => {
    const dateStartParam = searchParams.get('date_start');
    const dateEndParam = searchParams.get('date_end');

    if (dateStartParam && dateEndParam) {
      return { date_start: dateStartParam, date_end: dateEndParam };
    }

    const currentDate = new Date();
    const firstDayOfPreviousMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() - 1,
      1,
    );
    const lastDayOfPreviousMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);

    return {
      date_start: moment(firstDayOfPreviousMonth).format('YYYY-MM-DD'),
      date_end: moment(lastDayOfPreviousMonth).format('YYYY-MM-DD'),
    };
  };

  const [filters, setFilters] = useState<IDossieFilters>(getDefaultFilters);

  // Fetch company info if not passed via state
  useEffect(() => {
    if (!companyInfo && externalId) {
      const fetchCompany = async () => {
        setIsLoadingCompany(true);
        try {
          const res = await api.get('/api/v1/empresas/', {
            params: { external_id: externalId },
          });
          const company = res.data?.data?.items?.[0];
          if (company) {
            setCompanyInfo({
              external_id: company.external_id,
              razao_social: company.razao_social || company.nome,
              nome: company.nome,
              cnpj: company.cnpj,
              regime_tributario_atualizado: company.regime_tributario_atualizado,
            });
          } else {
            toast({
              title: 'Empresa não encontrada',
              status: 'error',
              duration: 5000,
            });
          }
        } catch (error) {
          console.error('Erro ao buscar empresa:', error); // eslint-disable-line no-console
          toast({
            title: 'Erro ao carregar empresa',
            status: 'error',
            duration: 5000,
          });
        } finally {
          setIsLoadingCompany(false);
        }
      };
      fetchCompany();
    }
  }, [externalId, companyInfo, toast]);

  // Fetch all dossie data
  useEffect(() => {
    if (!externalId || !companyInfo || isFetching.current) return;
    isFetching.current = true;

    const fetchDossieData = async () => {
      setIsLoadingData(true);

      const taxParams = getTaxParams(companyInfo.regime_tributario_atualizado);

      // Extrai o ano do filtro para a consulta de folha
      const anoFolha = parseInt(filters.date_start.split('-')[0], 10);

      try {
        const [faturamentoRes, impostosRes, folhaRes] = await Promise.all([
          // Faturamento list - filtro por período no backend
          getFaturamentoList({
            external_id: externalId,
            date_start: filters.date_start,
            date_end: filters.date_end,
          }).catch(err => {
            console.error('Erro ao buscar faturamento:', err); // eslint-disable-line no-console
            return { data: { items: [] } };
          }),

          // Impostos totals
          getImpostosTotais({
            external_id: externalId,
            date_start: filters.date_start,
            date_end: filters.date_end,
            ...taxParams,
          }).catch(err => {
            console.error('Erro ao buscar impostos:', err); // eslint-disable-line no-console
            return null;
          }),

          // Folha por mês - nova rota que retorna dados agrupados por mês
          getFolhaPorMes({
            external_id: externalId,
            ano: anoFolha,
          }).catch(err => {
            console.error('Erro ao buscar folha:', err); // eslint-disable-line no-console
            return { data: null };
          }),
        ]);

        // Process responses - handle different response structures
        // A nova rota retorna { data: [...], meta: {} } onde data é um array direto
        // A rota antiga retornava { data: { items: [...], ... } }
        let faturamentoItems: IFaturamentoItem[] = [];
        if (faturamentoRes.data) {
          if (Array.isArray(faturamentoRes.data)) {
            // Nova estrutura: data é array direto
            faturamentoItems = faturamentoRes.data;
          } else if (faturamentoRes.data.items && Array.isArray(faturamentoRes.data.items)) {
            // Estrutura paginada antiga: data.items
            faturamentoItems = faturamentoRes.data.items;
          }
        }

        const impostosData: IImpostosTotals | null = impostosRes || null;
        const folhaData: IFolhaPorMesData | null = folhaRes.data || null;

        setDossieData({
          faturamento: faturamentoItems,
          impostos: impostosData,
          folha: folhaData,
        });
      } catch (error) {
        console.error('Erro ao buscar dados do dossiê:', error); // eslint-disable-line no-console
        toast({
          title: 'Erro ao carregar dados',
          description: 'Não foi possível carregar todos os dados do dossiê.',
          status: 'error',
          duration: 5000,
        });
      } finally {
        setIsLoadingData(false);
        isFetching.current = false;
      }
    };

    fetchDossieData();
  }, [externalId, companyInfo, filters, toast]);

  // Handle filter change
  const handleFilterChange = (newFilters: IDossieFilters) => {
    isFetching.current = false; // Reset para permitir nova busca
    setSearchParams({
      date_start: newFilters.date_start,
      date_end: newFilters.date_end,
    });
    setFilters(newFilters);
  };

  // Export handlers
  const handleExportPDF = async () => {
    if (!companyInfo) return;
    setIsExporting(true);
    try {
      await exportDossieToPDF('dossie-dashboard-content', companyInfo, filters);
      toast({
        title: 'Exportação concluída',
        description: 'O arquivo PDF foi baixado com sucesso.',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: 'Erro na exportação',
        description: 'Não foi possível gerar o arquivo PDF.',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Loading state for company info
  if (isLoadingCompany) {
    return (
      <PageBox>
        <Flex justify="center" align="center" py={20}>
          <Spinner size="xl" color="blue.500" thickness="3px" />
        </Flex>
      </PageBox>
    );
  }

  return (
    <PageBox>
      <Box maxW="1600px" mx="auto">
        <DossieHeader
          companyInfo={companyInfo}
          onExportPDF={handleExportPDF}
          isExporting={isExporting}
          onSendToClient={onOpenEnviarModal}
          onViewLogs={onOpenLogsModal}
        />

        <DossieFilters filters={filters} onFilterChange={handleFilterChange} />

        <DossieDashboard data={dossieData} isLoading={isLoadingData} />
      </Box>

      {/* Modal de enviar ao cliente */}
      {companyInfo && (
        <EnviarClienteModal
          isOpen={isEnviarModalOpen}
          onClose={onCloseEnviarModal}
          codiEmp={parseInt(companyInfo.external_id, 10)}
        />
      )}

      {/* Modal de logs de links */}
      {companyInfo && (
        <LinksLogsModal
          isOpen={isLogsModalOpen}
          onClose={onCloseLogsModal}
          codiEmp={parseInt(companyInfo.external_id, 10)}
        />
      )}
    </PageBox>
  );
}
